import type { BundleOfferDto } from '@/queries/bundles/bundleOffers.schema';

export interface BundleGroup {
  parent: BundleOfferDto;
  children: BundleOfferDto[];
}

/**
 * The product-level display order (`product.presentation.order`). It ranks only
 * the "big" top-level items — parents and standalones (and VIP bundles in the Shop
 * modal). Child items inside a parent's drawer ignore it and keep the index order
 * the backend lists them in.
 */
export const orderIt = (offer: BundleOfferDto): number => offer.product.presentation?.order ?? 0;

/**
 * Collapses promo offers into a single "headline + drawer" card. Unlike base
 * bundles (which group via `relatedProducts`), promos are grouped purely by order:
 * the promo with the lowest `orderIt` becomes the parent big card (the first one
 * wins on ties, in input order), and every other promo becomes a child that follows
 * in the caller's ascending `orderIt` order. Returns null when there are no promo offers.
 */
export const buildPromoBundleGroup = (promoOffers: BundleOfferDto[]): BundleGroup | null => {
  let parent: BundleOfferDto | null = null;
  for (const offer of promoOffers) {
    if (parent === null || orderIt(offer) < orderIt(parent)) {
      parent = offer;
    }
  }

  if (parent === null) {
    return null;
  }

  const parentOffer = parent;

  return {
    parent: parentOffer,
    children: promoOffers.filter(offer => offer !== parentOffer),
  };
};
